import { Injectable, WritableSignal, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { GqlParser, MutationResult } from '@unipin/angular-gql';
import { AppLoginSubject, ChangePassRq, ForgotPassOtpRq, ForgotPassRq, LoginResponse, MutationAuthMsRefreshAccessTokenArgs, RegistrationRq, SocialLoginRq, SourcePlatform, SuggestedAction, Token, UserConfirmRq } from 'graphql/generated';
import { User } from '@core/interfaces/user.interface';

@Injectable({ 
  providedIn: 'root' 
})
export class AuthService {

  public static authToken: string = '';
  public static refreshToken: string = '';
  public user: WritableSignal<User> = signal({ birthday: '', email: '', gender: '', guid: '', name: '', photo: '', category: '', mobileNumber: '', isSocialLogin: false });
  protected readonly userBaseAttr: string = 'birthday category gender guid name status';
  protected readonly baseAttr: string = `accessToken refreshToken user { ${ this.userBaseAttr } email mobileNumber appLoginExt { paramCode file { publicUrl } } }`;

  constructor(
    protected readonly gql: GqlParser
  ) { }

  public register(input: RegistrationRq): Observable<MutationResult<{ authMsRegister: AppLoginSubject }>>{
    return this.gql.mutate({
      operationName: 'Register',
      variables: { 
        input 
      },
      mutation: `
        mutation Register($input: RegistrationRq!) {
          authMsRegister(input: $input) {
            ${ this.userBaseAttr }
          }
        }
      `
    });
  }

  public login(cred: string, password: string, type: 'email' | 'mobileNumber'): Observable<MutationResult<{ authMsLogin: LoginResponse }>>{
    return this.gql.mutate<{ authMsLogin: LoginResponse }>({
      operationName: 'Login',
      variables: { 
        input: { 
          password,
          sourcePlatform: SourcePlatform.CpCommerce,
          [type]: cred
        }
      },
      mutation: `
        mutation Login($input: LoginRq!) {
          authMsLogin(input: $input) {
            ${ this.baseAttr }
            suggestedAction
          }
        }
      `
    }).pipe(
      tap(({ data }) => {
        if (data?.authMsLogin) {
          this.setUser(data.authMsLogin);
        }
      })
    );
  }

  public socialLogin(input: SocialLoginRq): Observable<MutationResult<{ authMsSocialLogin: LoginResponse }>>{
    return this.gql.mutate<{ authMsSocialLogin: LoginResponse }>({
      operationName: 'SocialLogin',
      variables: { 
        input 
      },
      mutation: `
        mutation SocialLogin($input: SocialLoginRq!) {
          authMsSocialLogin(input: $input) {
            ${ this.baseAttr }
          }
        }
      `
    }).pipe(
      tap(({ data }) => {
        if (data?.authMsSocialLogin) {
          this.setUser(data.authMsSocialLogin);
        }

        this.user.set(
          { 
            ...this.user(),
            isSocialLogin: true 
          }
        );
      })
    );
  }

  public resendVerification(cred: string, strategy: 'email' | 'mobileNumber'): Observable<MutationResult<{ [k: string]: string }>>{
    const mutationStr: string = strategy === 'email'? 'authMsResendEmailVerif(email:' : 'authMsResendMobileVerif(mobileNum:';

    return this.gql.mutate({
      operationName: 'ResendVerification',
      variables: { 
        cred, 
        source: SourcePlatform.CpCommerce 
      },
      mutation: `
        mutation ResendVerification($cred: String!, $source: SourcePlatform!) {
          ${ mutationStr } $cred, sourcePlatform: $source)
        }
      `
    });
  }

  public confirmVerification(input: UserConfirmRq): Observable<MutationResult<{ authMsUserConfirm: string }>>{
    return this.gql.mutate({
      operationName: 'UserConfirm',
      variables: { 
        input 
      },
      mutation: `
        mutation UserConfirm($input: UserConfirmRq!) {
          authMsUserConfirm(input: $input)
        }
      `
    });
  }

  public reqForgotPass(input: ForgotPassOtpRq): Observable<MutationResult<{ authMsForgotPasswordOtp: string }>>{
    return this.gql.mutate({
      operationName: 'ReqForgotPass',
      variables: { 
        input 
      },
      mutation: `
        mutation ReqForgotPass($input: ForgotPassOtpRq!) {
          authMsForgotPasswordOtp(input: $input)
        }
      `
    });
  }

  public confirmForgotPass(input: ForgotPassRq): Observable<MutationResult<{ authMsUserConfirm: string }>>{
    return this.gql.mutate({
      operationName: 'ConfirmForgotPass',
      variables: { 
        input 
      },
      mutation: `
        mutation ConfirmForgotPass($input: ForgotPassRq!) {
          authMsForgotPassword(input: $input)
        }
      `
    });
  }

  public updatePassword(input: ChangePassRq): Observable<MutationResult<{ authMsChangePassword: string }>>{
    return this.gql.mutate({
      operationName: 'ChangePassword',
      variables: { 
        input 
      },
      mutation:`
        mutation ChangePassword($input: ChangePassRq!) {
          authMsChangePassword(input: $input)
        }
      `
    });
  }

  public refreshToken(variables: MutationAuthMsRefreshAccessTokenArgs): Observable<MutationResult<{ authMsRefreshAccessToken: Token }>>{
    return this.gql.mutate({
      variables,
      operationName: 'RefreshToken',
      headers: {
        'operation-type': 'Mutation',
        'operation-name': 'RefreshToken'
      },
      mutation: `
        mutation RefreshToken($refreshToken: String!) {
          authMsRefreshAccessToken(refreshToken: $refreshToken) {
            accessToken
            refreshToken
          }
        }
      `
    });
  }

  public logout(): void{
    localStorage?.removeItem('userGuid');
    localStorage?.removeItem('authToken');
    localStorage?.removeItem('refreshToken');
    
    AuthService.authToken = AuthService.refreshToken = '';
    this.user.set({ birthday: '', email: '', gender: '', guid: '', name: '', photo: '', category: '', mobileNumber: '', isSocialLogin: false });
  }

  public setUser({ user, accessToken, suggestedAction }: LoginResponse): void{
    const photo = user!.appLoginExt?.find(({ paramCode }: any) => paramCode === 'USER_PROFILE_PHOTO')?.file?.publicUrl;
    this.user.set({ ...user as User , photo: photo?? '/assets/images/avatar.webp' });
    
    AuthService.authToken = accessToken?? '';
    localStorage?.setItem('userGuid', user!.guid!);
    
    if (
      suggestedAction !== SuggestedAction.ChangePassword &&
      localStorage
    ) { 
      localStorage.setItem('authToken', AuthService.authToken); 
      localStorage.setItem('refreshToken', AuthService.refreshToken);
    }
  }
}
